import "../../../common/polyfills";
import * as React from "react";
import { useState } from "react";
import * as moment from "moment";
import { sanitize } from "dompurify";
import { IFormProps, IPaymentFormProps } from "../../../common/form/FormConfig";
import EventFormWithstripeInjector, { EventForm } from "./EventForm";
import { IStandardFormProps } from "../../GenericForm/Components/Constants";
import { StripeProvider, Elements } from "react-stripe-elements";
import { useFetchCCEvent, IGetCCEventResponse } from "./EventRegFormHelper";

export interface CCEventRegFormProps extends IStandardFormProps, IFormProps, IPaymentFormProps {
	EventId?: number | string;
	Id?: number | string;
	hidePayModal?: () => void;
	formHeader?: string;
	formHeaderSubtext?: string;
	paypalClientId?: string;
	rootNode: string;
	declarationText: string;
}

export function purifyHtml(html: HTMLElement | string) {
	let clean = sanitize(html, { FORBID_TAGS: ["style"], FORBID_ATTR: ["style"] });
	return { __html: clean };
}

interface IEventDetailProps {
	event: IEventProps;
	className: string;
}
export interface IEventProps {
	ContentId: number;
	SharedId?: number;
	HomeContentId?: number;
	EventID: number;
	StartDate: Date;
	EndDate: Date;
	Name: string;
	Latitude: number;
	Longitude: number;
	Location: string;
	TotalTickets: number;
	TicketsLeft: number;
	MaxTicketsPerRegistration: number;
	RegistrationInputMask: number;
	ClosingDateOfRegistration: Date;
	NotifyEmails: string;
	AutoRespondEmailFrom: string;
	AutoRespondEmailTemplate: string;
	AutoRespondEmailSubject: string;
	TicketPrice: number;
	Description: string;
	Created?: Date;
	Modified?: Date;
	Deleted: boolean;
	CCKey: string;
}

const EventDetail = (props: IEventDetailProps) => {
	const { event } = props;
	var localStartDate = moment(event && event.StartDate).format("dddd, DD MMM YYYY");
	var localStartTime = moment(event && event.StartDate).format("h:mm a");
	var localEnd = moment(event && event.EndDate).format("hh:mm a");
	var ticketPrice = event && event.TicketPrice ? event.TicketPrice : 0;
	return (
		<div className={props.className}>
			<h5 className="detail-label">Event Details:</h5>
			<div className="event-detail event-time">
				<div className="detail-value">
					{localStartDate},
					<span className="time">
						{localStartTime} - {localEnd}
					</span>
				</div>
			</div>
			{event && event.Location && (
				<div className="event-detail event-location">
					<div className="detail-value">
						<span className="location">{event.Location}</span>
					</div>
				</div>
			)}
			{ticketPrice > 0 && (
				<div className="event-detail event-description">Ticket Price: ${ticketPrice}</div>
			)}
			<hr />
			{event && event.Description && (
				<div className="event-detail event-description">
					<div
						className="detail-value"
						dangerouslySetInnerHTML={purifyHtml(event.Description)}
					></div>
				</div>
			)}
		</div>
	);
};

export const CCEventRegForm = (props: CCEventRegFormProps) => {
	const {
		EventId,
		contentId,
		publishableKey,
		formHeader,
		formHeaderSubtext,
		paymentProviderName,
		paypalClientId,
		autocomplete,
	} = props;
	const [isSubmitted, setIsSubmitted] = useState(false);
	const eventData = useFetchCCEvent(EventId, contentId, isSubmitted);
	const ticketPrice =
		eventData && eventData.success && eventData.data.TicketPrice
			? eventData.data.TicketPrice
			: 0;
	const maxTicketsPerRegistration =
		eventData && eventData.success && eventData.data.MaxTicketsPerRegistration
			? eventData.data.MaxTicketsPerRegistration
			: 1;
	const ticketsLeft =
		eventData && eventData.success && eventData.data.TicketsLeft
			? eventData.data.TicketsLeft
			: 0;
	const totalTickets =
		eventData && eventData.success && eventData.data.TotalTickets
			? eventData.data.TotalTickets
			: 0;
	return (
		<div className="row">
			<EventDetail className="col-12 col-md-6 event-info" event={eventData.data} />
			{paymentProviderName == "stripe" && publishableKey ? (
				<>
					<StripeProvider apiKey={publishableKey}>
						<Elements>
							<div className="col-12 col-md-6 event-reg-form">
								<EventFormWithstripeInjector
									{...props}
									setIsSubmitted={setIsSubmitted}
									totalTickets={totalTickets}
									ticketsLeft={ticketsLeft}
									ticketCost={ticketPrice}
									maximumTicketsPerReg={maxTicketsPerRegistration}
									paymentProvider={paymentProviderName}
									fieldsAsColumn
									formHeader={formHeader}
									headerSubtext={formHeaderSubtext}
									paypalClientId={paypalClientId}
									autocomplete={autocomplete}
								/>
							</div>
						</Elements>
					</StripeProvider>
				</>
			) : (
				<div className="col-12 col-md-6 event-reg-form">
					<EventForm
						{...props}
						ticketCost={ticketPrice}
						setIsSubmitted={setIsSubmitted}
						totalTickets={totalTickets}
						ticketsLeft={ticketsLeft}
						maximumTicketsPerReg={maxTicketsPerRegistration}
						paymentProvider={paymentProviderName}
						fieldsAsColumn
						formHeader={formHeader}
						headerSubtext={formHeaderSubtext}
						paypalClientId={paypalClientId}
						autocomplete={autocomplete}
					/>
				</div>
			)}
		</div>
	);
};
